/* eslint-disable react/prop-types */
/* eslint-disable react/react-in-jsx-scope */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Suspense, useEffect, useState } from "react";

import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSidebar } from "@/components/ui/sidebar";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { convertEuroToCurrency, convertToEuro } from "@/libs/amount";
import { Project, Project as ProjectType } from "@/types/pp";
import { format } from "date-fns";
import { Info } from "lucide-react";
import * as turf from "@turf/turf";
import DataReporting from "./DataReporting";
import FundingPieProject from "./FundingPieProject";
import SummarySheetRight from "./SummarySheetRight";
import TextInfo from "./TextInfo";

const Fiche = ({ project }: { project: ProjectType | null }) => {
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lon: number;
  } | null>(null);

  const [symbolDevise, setSymbolDevise] = useState("€");
  const { data: programReportData, loading: loadingReport } =
    useFetchDataV2<any>(
      project?.program?.id
        ? `${import.meta.env.VITE_API_ENDPOINT}/report/programs?program=${project.program.id
        }`
        : null,
      `program-${project?.program?.id}`
    );

  const [loadTimeoutReport, setLoadTimeoutReport] = useState(true);
  useEffect(() => {
    if (!loadingReport) {
      const timeout = setTimeout(() => {
        setLoadTimeoutReport(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [loadingReport]);

  const { open: openSidebar } = useSidebar();

  const [totalDesiredFunding, setTotalDesiredFunding] = useState(0);
  const [totalObtainedFunding, setTotalObtainedFunding] = useState(0);
  const [percentFunding, setPercentFunding] = useState(0);
  const [textValueDevise, setTextValueDevise] = useState("");
  const [valueAC, setValueAC] = useState(0); // Valeur réelle (en euros)
  const [valueCO, setValueCO] = useState(0); // Valeur réelle (en euros)

  const [selectedCurrency, setSelectedCurrency] = useState("EUR");
  const localDevise = project?.country?.currency;
  const [surface, setSurface] = useState(0);
  const { data: foldersData } = useFetchDataV2<any[]>(
    project?.id
      ? `${import.meta.env.VITE_API_ENDPOINT}/folders/${project.id}`
      : null,
    project?.id ? `folders-${project.id}` : undefined,
    1000 * 60 * 60,
  );

  async function calculateFunding(project: Project) {
    // 1) Budget FS (hors FSV) en EUR
    let desiredBudgetEur = 0;
    for (const fund of project?.desiredFunding ?? []) {
      const amountEur = await convertToEuro(fund.amount, fund.devise);
      desiredBudgetEur += Number.isFinite(amountEur) ? amountEur : 0;
    }

    // 2) Contributions obtenues "classiques" en EUR
    let obtainedEur = 0;
    for (const fund of project?.obtainedFunding ?? []) {
      const ownRate =
        fund?.obtainedDate && fund?.obtainedRate ? fund.obtainedRate : undefined;
      const amountEur = await convertToEuro(
        fund.amount,
        fund.devise,
        ownRate
      );
      obtainedEur += Number.isFinite(amountEur) ? amountEur : 0;
    }

    // 3) Part Verdeo (inclut FSV) en EUR
    let verdeoWithFsvEur = 0;
    if (project?.verdeoFundingInvoiceds) {
      for (const invoice of project.verdeoFundingInvoiceds) {
        const fs = Number(invoice?.fs ?? 0);
        const fsv = Number(invoice?.fsv ?? 0);
        verdeoWithFsvEur +=
          (Number.isFinite(fs) ? fs : 0) + (Number.isFinite(fsv) ? fsv : 0);
      }
    }

    // 4) Totaux de synthèse (admin/front) :
    // AC = FS + FSV (15%) restant après ce qui est déjà comptabilisé via Verdeo
    // CO = obtenus + part Verdeo incluant FSV
    const totalObtainedEur = obtainedEur + verdeoWithFsvEur;
    const totalDesiredEur = Math.max(desiredBudgetEur * 1.15 - verdeoWithFsvEur, 0);

    // 5) Gestion devise / affichage
    const currency = project?.country?.currency ?? { code: "EUR", symbol: "€" };
    const date = new Date().toISOString().split("T")[0];

    if (selectedCurrency === "EUR" || !localDevise) {
      setTotalDesiredFunding(totalDesiredEur);
      setTotalObtainedFunding(totalObtainedEur);

      if (localDevise) {
        const valueToEuro = await convertEuroToCurrency(1, localDevise);
        const text = `1 EUR = ${parseFloat(
          valueToEuro.toFixed(1)
        ).toLocaleString("fr-FR")} ${currency.code} en date du ${date}`;
        setTextValueDevise(text);
      } else {
        setTextValueDevise("");
      }
    } else {
      const newTdf = await convertEuroToCurrency(
        totalDesiredEur,
        localDevise
      );
      setTotalDesiredFunding(newTdf);

      const newTOF = await convertEuroToCurrency(
        totalObtainedEur,
        localDevise
      );
      setTotalObtainedFunding(newTOF);

      const valueToEuro = await convertToEuro(1, localDevise);
      const text = `1 ${currency.code} = ${parseFloat(
        valueToEuro.toFixed(1)
      ).toLocaleString("fr-FR")} EUR en date du ${date}`;
      setTextValueDevise(text);
    }

    // 6) Valeurs reporting en EUR
    setValueCO(totalObtainedEur);
    setValueAC(totalDesiredEur);
  }

  useEffect(() => {
    if (selectedCurrency == "EUR") {
      setSymbolDevise("€");
    } else {
      setSymbolDevise(localDevise?.symbol);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    if (project) {
      calculateFunding(project);
    }
  }, [project, selectedCurrency]);

  useEffect(() => {
    const denominator = totalObtainedFunding + totalDesiredFunding;
    if (denominator <= 0) {
      setPercentFunding(0);
      return;
    }
    setPercentFunding(Number(((totalObtainedFunding / denominator) * 100).toFixed(1)));
  }, [totalObtainedFunding, totalDesiredFunding]);

  useEffect(() => {
    if (!foldersData) return;

    let surfaceTemp = 0;
    const existingPolygons = foldersData.flatMap((folder: any) => [
      ...(folder.locations || []),
      ...(folder.parcels || []),
    ]);

    existingPolygons.forEach((polygon: any) => {
      const correctedCoordinates = polygon?.coordinates?.map((coord: any) => [
        coord[1],
        coord[0],
      ]);

      if (!Array.isArray(correctedCoordinates) || correctedCoordinates.length < 3) {
        return;
      }

      const firstElement = correctedCoordinates[0];
      const turfPolygon = turf.polygon([[...correctedCoordinates, firstElement]]);
      surfaceTemp += turf.area(turfPolygon);
    });

    setSurface(surfaceTemp);
  }, [foldersData]);

  const formatSummaryCurrency = (value: number) =>
    parseFloat(value.toFixed(1)).toLocaleString("fr-FR") + " " + symbolDevise;

  return (
    <div className="mt-4">
      <div className="flex flex-row gap-6 h-auto ">
        <div className="basis-1/4">
          <Photos
            photo={
              project?.defaultImage
                ? project?.defaultImage
                : project?.type?.image
            }
          />
        </div>
        <div className="h-30 basis-2/4">
          <div className="flex flex-row justify-between">
            <TextInfo className="" title={project?.name} text="" />
          </div>

          <div>
            <div className=" h-30 text-[#878787] overflow-auto whitespace-pre-line">
              <p>{project?.description}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-rows-4 gap-3 max-h-70 rounded-[20px] p-4 basis-1/4 bg-[#F7F9FB]">
          <div style={{ position: "relative" }}>
            <select
              value={selectedCurrency}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCurrency(value);
                // handleDeviseChange(indicator, value);
              }}
              style={{
                position: "absolute",
                right: "0",
                top: "-9px",
                width: "83px",
                height: "30px",
                marginTop: "5px",
                zIndex: "1000",
              }}
            >
              <option
                value="EUR"
                style={{ color: "hsl(var(--foreground) / 0.3)" }}
              >
                €
              </option>
              {project?.country?.currency?.code !== "EUR" && (
                <option value={project?.country?.currency?.code}>
                  {project?.country?.currency?.symbol}
                </option>
              )}
            </select>
            <div className="flex flex-wrap">
              {project?.country?.currency?.code !== "EUR" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Info
                      size={18}
                      className="cursor-pointer font-bold text-[#292d42]"
                      style={{
                        marginTop: "5px",
                        marginRight: "8px",
                        marginLeft: "-11px",
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="z-[99999] text-xs">
                    <p className="m-0">{textValueDevise}</p>
                  </PopoverContent>
                </Popover>
              )}
              <TextInfo
                className="text-xl text-[#ff0000]"
                title={
                  parseFloat(totalDesiredFunding.toFixed(1)).toLocaleString(
                    "fr"
                  ) + symbolDevise
                }
                text="Appel à contributions"
              />
            </div>
          </div>
          {(totalObtainedFunding != 0 || totalDesiredFunding != 0) && (
            <div className="relative left-2 mr-8 my-1">
              <FundingPieProject
                totalObtainedFunding={parseFloat(
                  totalObtainedFunding.toFixed(1)
                )}
                totalDesiredFunding={parseFloat(totalDesiredFunding.toFixed(1))}
                fees={0}
                devise={symbolDevise}
                type={1}
                className="h-[300px] w-[100%] absolute top-[-120px] right-[-10px]"
              />
            </div>
          )}

          <div className="rounded-[14px] border border-[#e4e9ef] bg-[#f2f6fa] px-3 py-2.5">
            <div className="min-w-0">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                Contributions déjà obtenues
              </p>
              <p className="mb-0 text-[20px] font-bold leading-none text-[#7DAA38]">
                {percentFunding}%
              </p>
            </div>
            <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-[#e8edf1]">
              <div
                className="h-full rounded-full bg-[#9dc95f] transition-all"
                style={{ width: `${Math.max(0, Math.min(percentFunding, 100))}%` }}
              />
            </div>
            <div className="mt-2.5 rounded-[10px] bg-[#f7f9fb] px-3 py-2">
              <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[#7A8491]">
                Montant déjà obtenu
              </p>
              <p className="mb-0 text-[15px] font-semibold text-[#1F2937]">
                {formatSummaryCurrency(totalObtainedFunding)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 ">
            <TextInfo
              className=""
              title="Date de Lancement"
              text={format(
                new Date(project?.launchDate as string),
                "dd/MM/yyyy"
              )}
            />
            <TextInfo
              className=""
              title="Durée prévue "
              text={project?.expectedDuration + " mois"}
            />
          </div>
        </div>
      </div>
      {/*  fin Description */}
      {/* Indicators */}
      {!loadTimeoutReport && (
        <div
          className={`overflow-hidden mt-5 mb-8
          ${openSidebar
              ? "max-w-[calc(100vw_-_var(--sidebar-width)_-_5rem)]"
              : "max-w-[calc(100vw_-_var(--sidebar-width-icon)_-_5rem)]"
            } `}
        >
          <h3 className="text-green-900 text-[16px] mt-3 mb-2 font-bold">
            IMPACT DE VOTRE FINANCEMENT
          </h3>
          <div
            className="flex w-full p-[20px] items-stretch rounded-[20px]  bg-[#F7F9FB]"
            id="project-cards"
          >
            <Suspense fallback={<LoadingSpinner />}>
              <DataReporting
                program={programReportData}
                project={project}
                valueAC={valueAC}
                valueCO={valueCO}
              />
            </Suspense>
          </div>
        </div>
      )}
      {/* fin  Indicators */}
      {/* Projet en un coup d'oeil */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        {/* colonne 1 */}
        <div className="relative">
          <div className="sticky top-0">
            <div className="rounded-[20px] bg-[#F7F9FB] p-4 ">
              <div className="flex flex-row justify-between">
                <h3 className="font-bold text-xl  text-green-950 mb-3">
                  Le projet en un coup d’oeil
                </h3>
              </div>
              <div className="px-3  inline-flex items-center justify-center rounded-full bg-[#80b533] h-7 text-white text-sm  mb-3">
                <p className="m-0">{project?.type?.name}</p>
              </div>

              <div>
                <TextInfo
                  className=""
                  title="Objectif à atteindre :"
                  text={project?.objective}
                />
                <TextInfo
                  className=""
                  title="Actions à mettre en œuvre :"
                  text={project?.actions}
                />
                <TextInfo
                  className=""
                  title="Bénéfices attendus:"
                  text={project?.benefits}
                />
                <TextInfo
                  className=""
                  title="Critères de performance :"
                  text={project?.criteria}
                />
              </div>
            </div>
            <div className="mt-4">
              <div>
                <TextInfo
                  className=""
                  title="Contributions déjà obtenues :"
                  text=""
                />
                <TabFunding
                  fundType="obtained"
                  fundings={project?.obtainedFunding}
                  key={"obtained"}
                />
              </div>
              <div className="mt-4">
                <TextInfo
                  className=""
                  title="Financements souhaités :"
                  text=""
                />
                <TabFunding
                  fundType="desired"
                  fundings={project?.desiredFunding}
                  key={"desired"}
                />
              </div>
            </div>
          </div>
        </div>
        {/*  fin colonne 1 */}

        {/* colonne 2*/}
        <SummarySheetRight
          carrier={project?.program.carrier}
          coordinates={coordinates}
          fundLocal={project?.fundLocal}
          manager={project?.manager}
          project={project}
          surface={surface}
        />
        {/*  fin colonne 2 */}
      </div>
    </div>
  );
};

const Photos = ({ photo }) => {
  return (
    <div className="group relative overflow-hidden rounded-[20px] items-center bg-[#f4f4f4]">
      <img
        src={import.meta.env.VITE_MEDIA_BASE + photo?.uri}
        alt={photo?.name}
      />
    </div>
  );
};

const TabFunding = ({ fundType, fundings }) => {
  const obtainedRow = ["Financeur", "Montant", "Date d'obtention"];
  const desiredRow = [
    "Actions à Financer",
    "Montant évalué",
    "pièce(s) explicative(s)",
  ];
  const visibleDesiredFundings = Array.isArray(fundings)
    ? fundings.filter((funding) => Number(funding?.amount ?? 0) >= 5)
    : [];
  const formatObtainedDate = (dateValue) => {
    if (!dateValue) return "-";

    const normalizedDate = String(dateValue).split(" ")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
      const [year, month, day] = normalizedDate.split("-");
      return `${day}/${month}/${year}`;
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "-";

    return format(parsedDate, "dd/MM/yyyy");
  };

  return (
    <div>
      <Table>
        <TableHeader style={{ backgroundColor: "gray" }}>
          <TableRow>
            {fundType == "obtained"
              ? obtainedRow.map((obtained, key) => (
                <TableHead className="text-white" key={key}>
                  {obtained}
                </TableHead>
              ))
              : desiredRow.map((desired, key) => (
                <TableHead className="text-white" key={key}>
                  {desired}
                </TableHead>
              ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {fundType == "obtained" &&
            fundings?.length > 0 &&
            fundings?.map((funding, key) => (
              <TableRow className="hover:bg-gray-200" key={funding?.id ?? key}>
                <TableCell>
                  {" "}
                  {funding?.financer
                    ? funding?.financer.fullname
                    : funding?.partner?.name}
                </TableCell>
                <TableCell>
                  {Math.floor(funding?.amount) + " " + funding?.devise?.symbol}
                </TableCell>
                <TableCell>{formatObtainedDate(funding?.obtainedDate)}</TableCell>
              </TableRow>
            ))}
          {fundType == "desired" &&
            visibleDesiredFundings.length > 0 &&
            visibleDesiredFundings.map((funding, key) => (
              <TableRow className="hover:bg-gray-200" key={key}>
                <TableCell>{funding?.title}</TableCell>
                <TableCell>
                  {Math.floor(funding?.amount) + " " + funding?.devise?.symbol}
                </TableCell>
                <TableCell className="flex flex-row">
                  {funding?.media?.length != 0 &&
                    funding?.media?.map((oneMedia, key) => (
                      <TooltipProvider key={key}>
                        <Tooltip>
                          <TooltipTrigger>
                            <a
                              href={`${import.meta.env.VITE_MEDIA_BASE}${oneMedia?.uri
                                }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#a2a0a0"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-file-down-icon lucide-file-down"
                              >
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                                <path d="M12 18v-6" />
                                <path d="m9 15 3 3 3-3" />
                              </svg>
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>{oneMedia?.name}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <div>
        {((fundType === "obtained" && fundings?.length == 0) ||
          (fundType === "desired" && visibleDesiredFundings.length === 0)) && (
            <p className="m-2 text-center"> Aucune donnée à afficher </p>
          )}
      </div>
    </div>
  );
};

export default Fiche;
